import { useEffect, useRef, useState } from 'react';

const DEFAULT_CHARS_PER_SECOND = 45;
const TICK_MS = 32; // ~30fps

type Options = {
  charsPerSecond?: number;
};

/**
 * 스트리밍 텍스트를 타이핑하듯 자연스럽게 보여주는 훅.
 * targetContent가 늘어날 때마다 displayedLength를 일정 속도로 따라가게 해 끊김을 줄인다.
 */
const useTypingEffect = (
  targetContent: string,
  isActive: boolean,
  options: Options = {}
): string => {
  const { charsPerSecond = DEFAULT_CHARS_PER_SECOND } = options;
  const [displayedLength, setDisplayedLength] = useState(0);
  const targetLengthRef = useRef(0);
  const displayedLengthRef = useRef(0);
  const rafRef = useRef<number | null>(null);

  targetLengthRef.current = targetContent.length;
  displayedLengthRef.current = displayedLength;

  useEffect(() => {
    if (!isActive) {
      setDisplayedLength(targetContent.length);
      displayedLengthRef.current = targetContent.length;
      return;
    }

    // target이 줄어든 경우만 즉시 맞춤
    if (targetContent.length < displayedLengthRef.current) {
      displayedLengthRef.current = targetContent.length;
      setDisplayedLength(targetContent.length);
    }

    const charsPerTick = Math.max(
      1,
      (charsPerSecond / 1000) * TICK_MS
    );

    const tick = () => {
      const targetLen = targetLengthRef.current;
      const current = displayedLengthRef.current;

      if (current >= targetLen) {
        rafRef.current = null;
        return;
      }

      const next = Math.min(targetLen, current + Math.ceil(charsPerTick));
      displayedLengthRef.current = next;
      setDisplayedLength(next);
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [isActive, targetContent.length, charsPerSecond]);

  useEffect(() => {
    if (!isActive) {
      setDisplayedLength(targetContent.length);
      displayedLengthRef.current = targetContent.length;
    }
  }, [isActive, targetContent.length]);

  return targetContent.slice(0, displayedLength);
};

export default useTypingEffect;
